import React from "react";
import { RouteComponentProps } from "@reach/router";
import { PolicyLayout } from "layouts";

export const PolicyPage: React.FC<RouteComponentProps> = () => {
  return (
    <PolicyLayout>
      <h1 className="mb-2 mt-0">JustLaw Privacy Policy</h1>
      <div className="mt-0 text-center">Effective Date: April 28, 2023</div>
      <div className="d-flex flex-column flex-1 overflow-auto px-2">
        <h2>1) Introduction</h2>
        <p className="mt-0">
          JustLaw (“JustLaw,” “we,” or “us”) respects the privacy of our customers
          and others who use our website, mobile applications, products, and
          services. This Privacy Policy applies to your access and use of the
          &nbsp;
          <a href="https://JustLaw.com">https://JustLaw.com</a> website (the
          “Site”) and the JustLaw application (the “Service”). This Privacy
          Policy is incorporated into and is subject to the JustLaw Terms of Use,
          which can be found at: <a href="/terms-of-use">Terms of Use</a>. This
          Privacy Policy is also incorporated into and is subject to JustLaw’s
          Customer Subscription Agreement, as applicable: [insert link, if
          agreement will be available online]. Within the Service, we may
          provide additional notices related to the privacy or protection of
          your data that are specific to certain practices or features of the
          Service. The terms of those notices are to be read and understood in
          conjunction with this Privacy Policy.
        </p>
        <p className="mt-0">
          Some information collected when you access or use our Site or Service
          is considered “Personal Information.” This is information that relates
          to or can be used to identify you personally and includes things like
          your full name, your street address, your phone number, and your email
          address, and also includes definitions for “personal information” or
          similar terms provided by data privacy laws applicable to JustLaw.
          Other than as provided for in this Privacy Policy, we won’t share or
          disclose this kind of information unless we get your permission. For
          purposes of this Privacy Policy, Personal Information does not include
          aggregated information, anonymous information or any other
          non-personally identifiable information, to the extent such is not
          protected by data privacy laws applicable to JustLaw.
        </p>
        <p className="mt-0">
          This Privacy Policy describes the information that we request or
          receive from you, how we use and disclose your information, and the
          steps we take to protect your information in connection with your use
          of the Site and the Service. It also tells you how you can access and
          update your information and make certain choices about how your
          information is used. By using the Site and/or Service in any manner,
          you consent to the privacy practices described in this Privacy Policy.
        </p>
        <p className="mt-0">
          In this Privacy Policy, you will find information concerning:
          <ul>
            <li>Information We Collect</li>
            <li>How We Use Information We Collect</li>
            <li>Information Sharing & Disclosure</li>
            <li>Your Choices & Rights</li>

            <li>Security</li>
            <li>Other Important Information</li>
            <li>Changes to This Privacy Policy</li>
            <li>How to Contact Us</li>
          </ul>
        </p>
        <h2>2) Information We Collect</h2>
        <p>
          This section describes the categories of information we collect when
          you use our Site and Service. The information we collect is provided
          by you, generated by the tracking technologies we may use.
        </p>
        <h3>2.1) Information You Provide</h3>
        <ul>
          <li>
            <i>Registration & Account Data</i>. When you register to use the
            Service or set up an account, you may provide and we may collect
            Personal Information (including potentially sensitive information),
            such as name, e-mail address, mailing address, mobile phone number,
            age, gender, date of birth, education information, information
            regarding fee arrangements, attorney bar registration information
            and other similar types of information.
          </li>
          <li>
            <i>Attorney-Client Communications:</i> When you register to use the
            Site or Service, you may voluntarily share information with any
            attorney or client, as applicable, that you choose to engage (or
            whom you are considering engaging) through a private chat function
            that includes (among other things) the ability to communicate via
            text, video and audio. To utilize these features, you may be asked
            to enable settings that allow other access to your device, such as
            access to your camera, microphone, or photos. Information exchanged
            in these private conversations may include (without limitation)
            video recordings, voice-recordings, text communications, Personal
            Information, and legal documents (“Attorney-Client Communications”).
            We will not access, use or disclose these Attorney-Client
            Communications except as expressly set forth in this Privacy Policy.
            For additional information regarding Attorney-Client Communications,
            please see our JustLaw Terms of Use.
          </li>
          <li>
            <i> Public Contributions.</i> When you register to use the Site or
            Service, you will have access to a variety of public chatrooms and
            other non-private areas of the Site or Service in which you may
            voluntarily share information, including by uploading content or
            other media. Examples may include unsolicited comments and
            suggestions, Personal Information, questions concerning your legal
            needs, or other questions or feedback related to the Service (as
            defined in our JustLaw Terms of Use, “Public Contributions”). In
            connection with using these features, you may be asked to enable
            settings that allow other access to your device, such as access to
            your camera, microphone, or photos. You should be cautious about
            what you post in publicly accessible areas of the Site or Service.
          </li>
          <li>
            <i>Transaction Information.</i> You may provide and we may collect
            Personal Information when you fill out forms or fields on the Site
            and within the Service and when you contact us or request
            information about us, this Site or our Service as well as when you
            purchase the Service
          </li>
          <li>
            <i> Usage Information.</i> When you visit the Site, use the Service,
            or open an email from us, we may collect and record (including
            automatically) certain information from your computer, web browser,
            mobile device and/or any devices you use that integrate with the
            Site or Service. We may combine this information across different
            devices you use. Information we obtain in this manner includes:
            <ul>
              <li>
                Identifiers and device attributes such as your IP address or
                other device address or ID, web browser and/or device type,
                hardware and software settings and configurations;
              </li>
              <li>
                Information about how you use and navigate the Site and Service,
                including the web pages or sites that you visit just before or
                just after visiting the Site, the pages you view on the Site and
                within the Service, your actions on the Site and within the
                Service, the dates and times that you visit, access, or use the
                Site and Service, which links you click on, which pages or
                content you view and for how long, and other similar information
                and statistics about your interactions with the Site and
                Service;
              </li>
              <li>
                Location information, including the physical location of your
                device. This information may be captured in a variety of ways,
                including through the use of tracking technologies such as
                Cookies (which are small files containing a string of
                alphanumeric characters) that we send to your computer or mobile
                device, web beacons or clear GIFs (which are small strings of
                code that provide a means to deliver a GIF for tracking
                purposes) that we embed on the Site, within the Service, or in
                emails we send to you, and similar technologies.
              </li>
            </ul>
          </li>
          <li>
            Cookies & Third Party Technologies. We may use a variety of cookies
            and third-party technologies to understand how you use and navigate
            the Site and Service. These may include Session, Persistent, and
            Flash Cookies and data analytics tools such as Google Analytics. A
            Session Cookie is temporary and disappears after you close your
            browser. A Persistent Cookie remains after you close your browser
            and may be used by your browser on subsequent visits to the Site or
            Service, including to remember your previous actions. Persistent
            Cookies can be removed. You may be able to configure your browser to
            accept or reject all Cookies or to notify you when a Cookie is set.
            Each browser is different. Please review your web browser “Help”
            file to learn the proper way to modify your Cookie settings. If you
            delete, or choose not to accept, Cookies from the Site or Service,
            you may not be able to utilize the features of the Site and Service
            to their fullest potential.
          </li>
        </ul>
        <p className="mt-0">
          Please note that “flash” Cookies are different from “browser” Cookies
          because of the way in which flash Cookies capture and store data. Your
          browser’s Cookie management tools will not remove flash Cookies. To
          learn how to manage privacy and storage settings for Flash Cookies
          click here:{" "}
          <a href="http://www.macromedia.com/support/documentation/en/flashplayer/help/settings_manager.html#117118">
            http://www.macromedia.com/support/documentation/en/flashplayer/help/settings_manager.html#117118
          </a>
          . We may use flash Cookies to provide some content such as video or
          animation.
        </p>
        <p className="mt-0">
          You may also visit the websites of the Network Advertising Initiative
          (
          <a href="https://www.networkadvertising.org">
            https://www.networkadvertising.org
          </a>
          ) or the Digital Advertising Alliance (
          <a href="https://digitaladvertisingalliance.org">
            https://digitaladvertisingalliance.org
          </a>
          ) for general information about your choices with respect to how
          information about your online activities is used.
        </p>
        <h3 className="mb-0">2.2) Information We Collect from Third Parties</h3>
        <p>[●]</p>
        <h2 className="mb-0">3) How We Use Information We Collect</h2>
        <p>
          This section describes the ways we use the various categories of
          information, described above, to operate our Site and Service.
        </p>
        <h3 className="mb-0">3.1) Information You Give to Us</h3>
        <p>
          <table>
            <thead>
              <tr>
                <th>Categories of Personal Data </th>
                <th>Purposes for Processing</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Registration and Account Information</td>
                <td></td>
              </tr>
              <tr>
                <td>Attorney-Client Communications</td>
                <td></td>
              </tr>
              <tr>
                <td>Public Communications</td>
                <td></td>
              </tr>
              <tr>
                <td>Transaction Information </td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </p>
        <h3 className="mb-0">
          3.2) Information We Collect Through Tracking Technologies
        </h3>
        <p>
          <table>
            <thead>
              <tr>
                <th>Categories of Personal Data </th>
                <th>Purposes for Processing</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Usage Information</td>
                <td></td>
              </tr>
              <tr>
                <td>Cookies & Third-Party Technologies</td>
                <td>
                  <div>
                    Operate, maintain, enhance, and provide all of the features
                    of the Site and Service;
                  </div>
                  <div>
                    Understand and analyze the usage trends and preferences of
                    our users, to improve the Site and Service, and to improve
                    fraud detection and information security;
                  </div>
                  <div>
                    Personalize the Site and Service, such as by remembering
                    your information and providing customized content;
                  </div>
                  <div>
                    Run analytics and study traffic patterns on the Site and
                    Service, including to monitor and analyze the effectiveness
                    of the Site and Service;
                  </div>
                  <div>
                    Tell if an email has been opened and acted upon; and
                  </div>
                  <div>Direct advertisements to you.</div>
                </td>
              </tr>
              <tr>
                <td>[●]</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </p>
        <p>
          <div>Information We Collect From Third Parties</div>
          <table>
            <thead>
              <tr>
                <th>Categories of Personal Data </th>
                <th>Purposes for Processing</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>[●]</td>
                <td>[●]</td>
              </tr>
            </tbody>
          </table>
        </p>
        <h2>4) Information Sharing & Disclosure</h2>
        <p>
          We do not sell your Personal Information. To make the Site and Service
          work, we may need to share your personal information with other users,
          third parties, and service providers. This section explains when and
          why we share your information.
        </p>
        <p>All Personal Information Except Attorney-Client Communications</p>
        <h3>4.1) General Disclosure Practices</h3>
        <p>
          We may disclose your information in connection with our business
          operations and the provision of the Site and Service.
          <ul>
            <li>
              Your Public Contributions will be available to anyone who is able
              to access that content, including other users of the Site or
              Service. We (as well as others with access to the information over
              whom we may not have any influence or control) may collect, store,
              and use this information in any way.
            </li>
            <li>
              We work with third party service providers to help us run our
              business. These third party service providers provide services
              that include (without limitation) website and application
              development and support, payment processors and financial services
              providers, IT providers (including hosting providers such as
              Amazon Web Services and support and maintenance providers), data
              storage, maintenance, legal advisors and other professional
              services, and marketing and promotions management. To the extent
              it is necessary for these third party service providers to
              complete their contractual obligations to us, these third parties
              may have access to or process your information. All third party
              service providers that process your personal information on our
              behalf are contractually obligated to respect the confidentiality
              of such personal information. These third party service providers
              may be located in a jurisdiction that does not provide the same
              level of data protection as the jurisdiction in which you reside.
            </li>
          </ul>
        </p>
        <h3>
          4.2) Compliance with Law, Responding to Legal Requests, and Protecting
          Our Rights
        </h3>
        We may disclose your information to law enforcement, regulators,
        governmental authorities, courts, tax authorities, attorneys, and others
        for the following reasons:
        <ul>
          <li>
            if we are required to do so by law or in the good-faith belief that
            such action is necessary to comply with law (including copyright
            law), in response to a court order, judicial or other government
            subpoena or warrant, or to otherwise cooperate with law or
            government enforcement activity;
          </li>
          <li>
            in our good faith belief that doing so is appropriate or necessary
            to take precautions against liability and/or to investigate and
            defend ourselves against any third party claims or allegations;
          </li>
          <li>
            to protect JustLaw from fraudulent, abusive, or unlawful uses or
            activity and to protect the security or integrity of the Service and
            the rights, property, or safety of JustLaw, our customers and users,
            or others.
          </li>
        </ul>
        <p>
          Where appropriate, we may notify users about legal requests unless:
          (i) providing notice is prohibited by the legal process itself, by
          court order we receive, or by applicable law, or (ii) we believe that
          providing notice would be futile, ineffective, create a risk of injury
          or bodily harm to an individual or group, or create or increase a risk
          of fraud upon JustLaw property, its users, and the Site and Service. In
          instances where we comply with legal requests without notice for these
          reasons, we will attempt to notify that user about the request after
          the fact where appropriate and where we determine in good faith that
          we are no longer prevented from doing so.
        </p>
        <h3>4.3) Business Transfers</h3>
        We understand that information exchanged with an attorney via a private
        chat function is subject to heightened confidentiality concerns and it
        is our policy to protect this information to the fullest extent
        permitted by law. For this reason, JusGlobal does not and will not
        access, use or disclose Attorney-Client Communications except:
        <ul>
          <li>
            as required by law or in the good faith belief that such action is
            necessary to comply with law, a court order, subpoena or warrant, or
            otherwise to cooperate with law enforcement, regulators,
            governmental authorities, courts, or other authorities, in which
            case we may access or use your information and disclose it to law
            enforcement, regulators, governmental authorities, courts, tax
            authorities, attorneys, or other authorities;
          </li>
          <li>
            as necessary to investigate or defend against any claims of
            illegality or wrongdoing (whether those claims relate to JustLaw’s
            actions or to your actions when using the Site or Service), in which
            case we may access or use your information and disclose it to law
            enforcement, regulators, governmental authorities, courts, tax
            authorities, attorneys, or others;
          </li>
          <li>
            as necessary to protect the security or integrity of the Service
            (including in connection with any information technology issue or
            instance of compromised security) or the rights, property, or safety
            of JustLaw’s our customers and users, or others, in which case we may
            access or use your information and disclose it to anyone reasonably
            necessary to assist us with these goals, including our IT providers;
          </li>
          <li>
            disclosure to our hosting providers, including Amazon Web Services,
            in connection with their provision of data hosting services; and
          </li>
          <li>
            in the event JustLaw undertakes or is involved in any merger,
            acquisition, reorganization, sale of assets, bankruptcy, or
            insolvency event, then we may sell, transfer or share some or all of
            our assets, including your information in connection with such
            transaction or in contemplation of such transaction (e.g., due
            diligence) with or to buyers or purchasers (or potential buyers or
            purchasers) and their representatives.
          </li>
        </ul>
        <h2>5) Your Choices and Rights</h2>
        <h3>5.1) Your Choices</h3>
        <ul>
          <li>
            You may, of course, decline to share certain information with us, in
            which case we may not be able to provide to you some of the features
            and functionality of the Site and Service. You can limit the
            information you provide to JustLaw. Participation in and interaction
            with various Site & Service features is voluntary.
          </li>
          <li>
            Once you have registered for a JustLaw account, you may update,
            correct, or delete your profile information and preferences at any
            time by accessing your account preferences page through the Site or
            Service.
          </li>
          <li>
            If you have not created an account and you would like for us to
            delete your information, please send us a request at{" "}
            <a href="mailto:info@JustLaw.com">info@JustLaw.com</a>
          </li>
          <li>
            If you do not wish to receive promotional communications, you can
            opt-out through links provided in email messages, by updating your
            account preferences, or by contacting us at{" "}
            <a href="mailto:info@JustLaw.com">info@JustLaw.com</a>. This opt-out
            will not apply to emails related to the completion of your
            registration, correction of user data, change of password, and other
            similar communications essential to your transactions. Your changes
            will be reflected promptly in active databases but we may retain all
            information you submit for a variety of purposes, including backups
            and archiving, prevention of fraud and abuse, and analytics.
          </li>
        </ul>
        <h3>5.2) California Residents</h3>
        <p>
          If you are a California resident, California Civil Code Section
          1798.83 permits you to request certain information regarding our
          disclosure of Personal Information to third parties for their direct
          marketing purposes. To make such a request, please send an email to{" "}
          <a href="mailto:info@JustLaw.com">info@JustLaw.com</a>, or you may
          contact us via regular mail at: One World Trade Center 285 Fulton
          Street 85th Floor Suite 8500 New York City NY 10007 USA.
          <p>
            Effective January 1, 2020, the California Consumer Privacy Act
            (CCPA) provides California residents with additional rights related
            to data privacy. If you have any questions about this section or
            whether it applies to you, please contact us at{" "}
            <a href="mailto:info@JustLaw.com">info@JustLaw.com</a>.
          </p>
          <p>
            Pursuant to the CCPA, upon making a verifiable request, California
            residents may:
            <ul>
              <li>
                request access to the specific pieces and categories of Personal
                Information that we have collected about you over the past
                twelve months, the categories of sources of that information,
                our business or commercial purposes for collecting the
                information, and the categories of third parties with whom the
                information was shared;
              </li>
              <li>
                submit a request for deletion of Personal Information, subject
                to certain exceptions, including (without limitation) in the
                event that we may need to retain Personal Information to
                complete the transaction for which the Personal Information was
                collected, detect security incidents or protect against illegal
                activity, exercise free speech, comply with a legal obligation,
                or for lawful internal use compatible with the context in which
                the information was provided by you. If your request is subject
                to any exception, we may deny your request to delete your data.
                Please note that you must verify your identity and request
                before further action is taken by us. To do so, we will notify
                you of what we require via email on{" "}
                <a href="mailto:info@JustLaw.com">info@JustLaw.com</a>
              </li>
            </ul>
          </p>
          To exercise these rights, you may contact us at: +1 212 220 6580. You
          have the right not to be discriminated against for exercising your
          rights under the CCPA. Consistent with California law, you may
          designate an authorized agent to make a request on your behalf. In
          order to designate an authorized agent Please contact us via email at
          <a href="mailto:info@JustLaw.com">info@JustLaw.com</a> or call us on + 1
          212 220 6580].
        </p>
        <h2>6) Security</h2>
        <p>
          We employ appropriate safeguards that are designed to protect the
          integrity and security of your information. No security measures are
          perfect or impenetrable, however, so we cannot ensure or warrant the
          security of any information you transmit to us through the Site or
          Service, and you do so at your own risk. We also cannot guarantee that
          such information may not be accessed, disclosed, altered, or destroyed
          by breach of any of our safeguards. We cannot control the actions of
          other users or other third parties with whom you may choose to share
          your information and therefore we cannot and do not guarantee that
          information you post through the Site or Service will not be viewed by
          unauthorized persons. We are not responsible for circumvention of any
          privacy settings or security measures contained on the Site or in the
          Service.
        </p>
        <p>
          We retain your information for as long as it is reasonably necessary
          and relevant for our operations, and/or to comply with legal or
          regulatory requirements, prevent fraud, resolve disputes, troubleshoot
          problems, assist with any investigation, or enforce our JustLaw Terms
          of Use or other agreements (including the JustLaw Customer Subscription
          Agreement, as applicable). After removal, copies of information that
          you have posted may remain viewable in cached and archived pages or if
          other users have copied or stored such information.
        </p>
        <h2>7) Other Important Information</h2>
        <h3>7.1) Visitors from Outside the United States</h3>
        <p>
          The Site and Service are controlled and operated by JustLaw in the
          United States and JustLaw may disclose information to third party
          service providers that are located in any country or jurisdiction
          across the globe. As such, your Personal Information may be
          transferred, stored and processed outside of your home jurisdiction.
          If you access or use the Site or Service from outside the United
          States, including (without limitation) from member states of the
          European Union, you acknowledge and unambiguously consent to the
          transfer of your information to, and its processing and storage in,
          the United States and potentially other jurisdictions that may not
          provide the same level of privacy and data protection as your home
          jurisdiction.
        </p>
        <p>
          Depending on the jurisdiction in which you reside, you may have the
          right to access, correct, delete, or restrict use of certain Personal
          Information covered by this Privacy Policy. Depending on the
          jurisdiction, you may also have the right to request that we refrain
          from processing your Personal Information. Please bear in mind that if
          you exercise such rights this may affect our ability to provide our
          products and services. For inquiries about your Personal Information,
          please contact us by e-mail at{" "}
          <a href="mailto:info@JustLaw.com">info@JustLaw.com</a>, or through any
          of the contact information provided below. While JustLaw will make
          reasonable efforts to accommodate your request, we also reserve the
          right to impose certain restrictions and requirements on such
          requests, if allowed or required by applicable laws. Please note that
          it may take some time to process your request, consistent with
          applicable law.
        </p>
        <h3>7.2) Limitation of Liability</h3>
        By accessing this Site or Service and/or otherwise providing us with
        Personal Information and other data, you expressly and unconditionally
        release and hold us harmless from any and all liability for any
        injuries, loss, or damage of any kind arising from or in connection with
        the use and/or misuse of such information. In addition, while we make
        efforts to ensure the proper and appropriate use of data by our service
        providers that may receive your information from us, we are not
        responsible for any injuries, loss, or damage of any kind arising from
        or in connection with the use and/or misuse of your information caused
        by those service providers.
        <h3>7.3) Third-Party Content</h3>
        Third-party content may be linked to or otherwise incorporated into the
        Site or the Service. For example, we may implement or allow third party
        advertising or other content on the Site and within the Service that may
        utilize web beacons, Cookies or similar tracking technologies placed or
        controlled by the third-party content provider. Additionally, we may
        implement third party buttons (such as Facebook “like” or “share”
        buttons) that may allow a third party to collect information about you
        through their browser Cookies, even when you do not interact with the
        button. Information collected by a third party in this manner is subject
        to that third party’s own data policies. JustLaw does not control and is
        not responsible for the content or privacy and data protection practices
        of third-party websites, applications, services, goods, or
        advertisements that may be linked to or otherwise incorporated into the
        Site or Service. If you link to or otherwise interact with third-party
        content, you do so at your own risk. JustLaw does not endorse, recommend,
        or make any representations or warranties regarding websites,
        applications, services, goods, or advertisements that may be linked to
        or otherwise incorporated into the Site or Service.
        <h3>7.4) Do Not Track Disclosures</h3>
        Third parties such as advertising networks, analytics providers, and
        others may collect information about your online activities over time
        and across different websites when you access or use the Site or
        Service. Currently, various browsers offer a Do Not Track (“DNT”)
        option, but there is no standard for how DNT should work on commercial
        websites. Due to lack of such standards, the Site does not respond to Do
        Not Track browser settings.
        <h3>7.5) Children’s Data</h3>
        <p>
          Protecting the privacy of young children is especially important. For
          that reason, we do not allow children under 13 years of age to use the
          Site or Service, we do not knowingly collect or maintain information
          from persons under 13 years of age, and no part of the Site or Service
          is directed to persons under 13 years of age. If you are under 13
          years of age, then please do not use or access the Site or Service at
          any time or in any manner. If we learn that information has been
          collected through the Site or Service from persons under 13 years of
          age and without verifiable parental consent, then we will take the
          appropriate steps to delete this information. If you are a parent or
          guardian and discover that your child under 13 years of age has
          obtained a JustLaw account or provided us with information, please
          alert us at <a href="mailto:info@JustLaw.com">info@JustLaw.com</a> to
          request that we delete the information from our systems.
        </p>
        <h3>8) Changes to this Privacy Policy</h3>
        We reserve the right to make changes to this Privacy Policy at any time.
        We will notify you about material changes in the way we treat your
        information, including by placing a prominent notice on the Site or by
        sending you an email so that you can choose whether to continue using
        the Site or Service. Material modifications are effective 30 calendar
        days after our initial notification or upon your acceptance of the
        modified Privacy Policy. Immaterial modifications are effective upon
        posting of the updated Privacy Policy on the Site. Please revisit this
        page periodically to stay aware of any changes to this Privacy Policy.
        For the avoidance of doubt, disputes arising hereunder will be resolved
        in accordance with the Privacy Policy in effect at the time the dispute
        arose.
        <h3>9) Contact Us</h3>
        <p>
          Please contact us with any questions or comments about this Privacy
          Policy, your Personal Information, our use and disclosure practices,
          or your consent choices by e-mail{" "}
          <a href="mailto:info@JustLaw.com">info@JustLaw.com</a> or mail at:
        </p>
        <p>
          JusGlobal LLC <br />
          Attn: Privacy <br />
          One World Trade Center <br />
          285 Fulton Street <br />
          85th Floor | Suite 8500 <br />
          New York City NY 10007 USA
        </p>
      </div>
    </PolicyLayout>
  );
};
